import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import SelectInput from '../../components/SelectInput';
import CKEditorComponent from '../../components/CKEditorComponent';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import workEducationApi from '../../api/workEducationApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const months = [
  { value: 'Jan', label: 'January' }, { value: 'Feb', label: 'February' },
  { value: 'Mar', label: 'March' }, { value: 'Apr', label: 'April' },
  { value: 'May', label: 'May' }, { value: 'Jun', label: 'June' },
  { value: 'Jul', label: 'July' }, { value: 'Aug', label: 'August' },
  { value: 'Sep', label: 'September' }, { value: 'Oct', label: 'October' },
  { value: 'Nov', label: 'November' }, { value: 'Dec', label: 'December' },
];

const EditWorkEducation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    designation: '',
    company_name: '',
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    content: '',
    is_active: 0,
  });
  const [entryFound, setEntryFound] = useState(true);

  useEffect(() => {
    const fetchEntry = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await workEducationApi.getWorkEducationByIdentifier(id);
        if (response.success && response.data) {
          const entryToEdit = response.data;
          setFormData({
            designation: entryToEdit.designation,
            company_name: entryToEdit.company_name,
            start_month: entryToEdit.start_month,
            start_year: entryToEdit.start_year,
            end_month: entryToEdit.end_month || '',
            end_year: entryToEdit.end_year || '',
            content: entryToEdit.content,
            is_active: entryToEdit.is_active ? 1 : 0,
          });
          setEntryFound(true);
        } else {
          setEntryFound(false);
          setGeneralError(response.message || 'Work/Education entry not found.');
        }
      } catch (err) {
        console.error('Failed to fetch work/education entry:', err);
        setEntryFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load work/education entry. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleContentChange = (content) => {
    setFormData({ ...formData, content: content });
    if (validationErrors.content) {
      setValidationErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    try {
      await workEducationApi.updateWorkEducation(id, formData);
      addToast('Work/Education entry updated successfully!', 'success');
      navigate(ROUTES.WORK_EDUCATION);
    } catch (err) {
      console.error('Failed to update work/education entry:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update work/education entry. Please try again.', 'error');
        setGeneralError(err.response?.data?.message || 'Failed to update work/education entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!entryFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Entry Not Found</h1>
        <p className="text-text-gray">{generalError || 'The work/education entry you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.WORK_EDUCATION)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Work & Education List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Work & Education Entry</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Designation"
          name="designation"
          id="designation"
          value={formData.designation}
          onChange={handleChange}
          required={true}
          placeholder="Designation"
          error={validationErrors?.designation?.[0]}
        />
        <TextInput
          label="Company Name"
          name="company_name"
          id="company_name"
          value={formData.company_name}
          onChange={handleChange}
          required={true}
          placeholder="Company Name"
          error={validationErrors?.company_name?.[0]}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Start Month"
            name="start_month"
            id="start_month"
            value={formData.start_month}
            onChange={handleChange}
            options={[{ value: '', label: 'Select Month' }, ...months]}
            required={true}
            error={validationErrors?.start_month?.[0]}
          />
          <TextInput
            label="Start Year"
            name="start_year"
            id="start_year"
            value={formData.start_year}
            onChange={handleChange}
            type="number"
            required={true}
            placeholder="YYYY"
            error={validationErrors?.start_year?.[0]}
          />
          <SelectInput
            label="End Month (Leave blank if Present)"
            name="end_month"
            id="end_month"
            value={formData.end_month}
            onChange={handleChange}
            options={[{ value: '', label: 'Select Month' }, ...months]}
            error={validationErrors?.end_month?.[0]}
          />
          <TextInput
            label="End Year (Leave blank if Present)"
            name="end_year"
            id="end_year"
            value={formData.end_year}
            onChange={handleChange}
            type="number"
            placeholder="YYYY"
            error={validationErrors?.end_year?.[0]}
          />
        </div>
        <CKEditorComponent
          label="Content"
          value={formData.content}
          onChange={handleContentChange}
          required={false}
          error={validationErrors?.content?.[0]}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Entry
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.WORK_EDUCATION)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditWorkEducation;